import React, { useState, useRef, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { SpecialForYouItem } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { formatRupiah } from '../../utils/formatters';
import {
  Sparkles,
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  Loader2,
  Check,
  Eye,
  EyeOff,
  Edit2,
  X,
  AlertCircle,
  Lock,
  Images,
} from 'lucide-react';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB existing project limit

export const SpecialForYouManager: React.FC = () => {
  const { data, auth, updateData, saveData, uploadImage, showToast } = useContent();

  const isSuperAdmin = auth.role !== 'outlet_admin';

  const [items, setItems] = useState<SpecialForYouItem[]>(() => {
    const raw = Array.isArray(data.specialForYou) ? data.specialForYou : [];
    return [...raw].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showLivePreview, setShowLivePreview] = useState<boolean>(true);

  // Modal / Form state for Add / Edit
  const [editingItem, setEditingItem] = useState<SpecialForYouItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formTitle, setFormTitle] = useState<string>('');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formImageUrl, setFormImageUrl] = useState<string>('');
  const [formPrice, setFormPrice] = useState<number>(0);
  const [formDisplayOrder, setFormDisplayOrder] = useState<number>(1);
  const [formIsActive, setFormIsActive] = useState<boolean>(true);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state when context data updates
  useEffect(() => {
    const raw = Array.isArray(data.specialForYou) ? data.specialForYou : [];
    setItems([...raw].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)));
  }, [data.specialForYou]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8 rounded-2xl bg-slate-900 border border-slate-800 text-center max-w-lg mx-auto my-12 space-y-3">
        <div className="w-12 h-12 rounded-2xl bg-rose-950/60 border border-rose-800/50 text-rose-400 flex items-center justify-center mx-auto">
          <Lock className="w-6 h-6" />
        </div>
        <h3 className="font-display font-bold text-lg text-white uppercase">
          Akses Terbatas — Khusus Admin Pusat
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed">
          Section CMS &ldquo;Spesial Untukmu&rdquo; bersifat global dan hanya dapat dikelola oleh Admin Pusat / Super Admin.
        </p>
      </div>
    );
  }

  const persistItems = async (newList: SpecialForYouItem[], notify = false): Promise<boolean> => {
    setIsSaving(true);
    try {
      const sorted = [...newList].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      let success = false;
      if (typeof updateData === 'function') {
        success = await updateData({
          specialForYou: sorted,
        });
      } else if (typeof saveData === 'function') {
        success = await saveData({
          ...data,
          specialForYou: sorted,
        });
      }

      if (notify) {
        if (success) {
          showToast('Data "Spesial Untukmu" berhasil disimpan ke database!', 'success');
        } else {
          showToast('Tersimpan di penyimpanan lokal.', 'info');
        }
      }
      return success;
    } catch (err: any) {
      console.error('[CMS SpecialForYou Error]:', err);
      showToast('Gagal menyimpan perubahan ke database.', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddModal = () => {
    const nextOrder =
      items.length > 0 ? Math.max(...items.map((item) => item.display_order || 0)) + 1 : 1;
    setEditingItem(null);
    setFormTitle('');
    setFormDescription('');
    setFormImageUrl('');
    setFormPrice(25000);
    setFormDisplayOrder(nextOrder);
    setFormIsActive(true);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: SpecialForYouItem) => {
    setEditingItem(item);
    setFormTitle(item.title || '');
    setFormDescription(item.description || '');
    setFormImageUrl(item.image_url || '');
    setFormPrice(Number(item.price ?? 0));
    setFormDisplayOrder(item.display_order || 1);
    setFormIsActive(item.is_active !== false);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setUploadError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const mimeLower = (file.type || '').toLowerCase();
    const extLower = (file.name.split('.').pop() || '').toLowerCase();
    const isAllowedExt = ['jpg', 'jpeg', 'png', 'webp'].includes(extLower);

    if (!ALLOWED_IMAGE_TYPES.includes(mimeLower) && !isAllowedExt) {
      setUploadError('Format file tidak didukung. Gunakan JPG, PNG, atau WEBP.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError('Ukuran file terlalu besar. Maksimal 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedUrl = await uploadImage(file, 'special_for_you');
      if (uploadedUrl) {
        setFormImageUrl(uploadedUrl);
        showToast('Foto Spesial Untukmu berhasil diupload!', 'success');
      } else {
        setUploadError('Gagal mengupload foto. Silakan coba lagi.');
      }
    } catch (err: any) {
      setUploadError('Terjadi kesalahan saat upload foto: ' + (err?.message || 'Error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveModalForm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTitle.trim()) {
      setUploadError('Nama / judul item wajib diisi.');
      return;
    }

    if (!formImageUrl.trim()) {
      setUploadError('Foto item wajib diupload.');
      return;
    }

    const nowIso = new Date().toISOString();
    let updatedList: SpecialForYouItem[];

    if (editingItem) {
      updatedList = items.map((item) => {
        if (item.id === editingItem.id) {
          return {
            ...item,
            title: formTitle.trim(),
            description: formDescription.trim(),
            image_url: formImageUrl.trim(),
            price: Math.max(0, Number(formPrice) || 0),
            display_order: Math.max(1, Number(formDisplayOrder) || 1),
            is_active: formIsActive,
            updated_at: nowIso,
          };
        }
        return item;
      });
    } else {
      const newItem: SpecialForYouItem = {
        id: `special-${Date.now()}`,
        title: formTitle.trim(),
        description: formDescription.trim(),
        image_url: formImageUrl.trim(),
        price: Math.max(0, Number(formPrice) || 0),
        display_order: Math.max(1, Number(formDisplayOrder) || 1),
        is_active: formIsActive,
        created_at: nowIso,
        updated_at: nowIso,
      };
      updatedList = [...items, newItem];
    }

    updatedList.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    setItems(updatedList);
    persistItems(updatedList, true);
    handleCloseModal();
  };

  const handleToggleActive = (id: string) => {
    const nowIso = new Date().toISOString();
    const updated = items.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          is_active: !item.is_active,
          updated_at: nowIso,
        };
      }
      return item;
    });
    setItems(updated);
    persistItems(updated, true);
  };

  const handleConfirmDelete = (id: string) => {
    const updated = items.filter((item) => item.id !== id);
    setItems(updated);
    setPendingDeleteId(null);
    persistItems(updated, true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const list = [...items];
    const temp = list[index];
    list[index] = list[index - 1];
    list[index - 1] = temp;

    const nowIso = new Date().toISOString();
    const reordered = list.map((item, idx) => ({
      ...item,
      display_order: idx + 1,
      updated_at: nowIso,
    }));

    setItems(reordered);
    persistItems(reordered, false);
  };

  const handleMoveDown = (index: number) => {
    if (index >= items.length - 1) return;
    const list = [...items];
    const temp = list[index];
    list[index] = list[index + 1];
    list[index + 1] = temp;

    const nowIso = new Date().toISOString();
    const reordered = list.map((item, idx) => ({
      ...item,
      display_order: idx + 1,
      updated_at: nowIso,
    }));

    setItems(reordered);
    persistItems(reordered, false);
  };

  const activeItemsPreview = items
    .filter((item) => item.is_active === true)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center border border-[#00E5FF]/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <h2 className="font-display font-black text-2xl text-white uppercase tracking-tight">
              CMS SPESIAL UNTUKMU
            </h2>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Kelola kurasi konten &ldquo;Spesial Untukmu&rdquo; di Homepage secara independen dan terpisah dari daftar Menu Produk.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => persistItems(items, true)}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 font-display font-bold text-xs tracking-wider uppercase flex items-center gap-2 shadow-lg shadow-[#00E5FF]/20 cursor-pointer disabled:opacity-50 transition-all active:scale-95"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{isSaving ? 'Menyimpan...' : 'SIMPAN KE DATABASE'}</span>
          </button>
        </div>
      </div>

      {/* Live Preview Card */}
      <div className="p-5 sm:p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00E5FF]" />
            <h3 className="font-display font-bold text-sm text-white uppercase tracking-wider">
              Live Preview Homepage ({activeItemsPreview.length} Item Aktif)
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShowLivePreview(!showLivePreview)}
            className="text-xs font-mono text-slate-400 hover:text-[#00E5FF] flex items-center gap-1.5 cursor-pointer"
          >
            {showLivePreview ? (
              <EyeOff className="w-3.5 h-3.5" />
            ) : (
              <Eye className="w-3.5 h-3.5 text-[#00E5FF]" />
            )}
            <span>{showLivePreview ? 'Sembunyikan' : 'Tampilkan'}</span>
          </button>
        </div>

        {showLivePreview && (
          <div className="pt-2">
            {activeItemsPreview.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                {activeItemsPreview.map((item) => (
                  <div
                    key={`preview-${item.id}`}
                    className="shrink-0 w-56 sm:w-60 bg-white rounded-2xl p-3 border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(15,30,54,0.05)] flex flex-col justify-between"
                  >
                    <div>
                      <div className="relative h-28 rounded-xl overflow-hidden mb-2.5 bg-slate-100">
                        <img
                          src={resolveMediaUrl(item.image_url)}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <h4 className="font-bold text-xs sm:text-sm text-[#0F1E36] leading-snug line-clamp-1">
                        {item.title}
                      </h4>
                      <p className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1">
                        {item.description || 'Menu spesial LetonKu'}
                      </p>
                    </div>

                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-extrabold text-[#1E3A8A] font-mono tabular-nums">
                        {formatRupiah(item.price)}
                      </span>
                      <span className="px-2.5 py-1 bg-[#EBF2FF] text-[#1E3A8A] text-[11px] font-bold rounded-lg">
                        Pesan
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 rounded-2xl bg-slate-950/80 border border-dashed border-slate-800 text-center text-slate-500 text-xs">
                Belum ada item &ldquo;Spesial Untukmu&rdquo; yang aktif. Homepage tidak akan menampilkan kartu pada section ini (tidak ada fallback otomatis ke Menu).
              </div>
            )}
          </div>
        )}
      </div>

      {/* Items List Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-base text-white uppercase tracking-wide">
              Daftar Spesial Untukmu ({items.length})
            </h3>
            <p className="text-xs text-slate-400">
              Tambah, ubah foto/nama/deskripsi/harga, atur urutan tampil, atau aktifkan/nonaktifkan item.
            </p>
          </div>

          <button
            type="button"
            onClick={handleOpenAddModal}
            id="add-special-for-you-btn"
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-[#00E5FF] text-xs font-bold font-mono uppercase flex items-center gap-2 transition-all cursor-pointer active:scale-95 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>+ Tambah Item</span>
          </button>
        </div>

        {items.length === 0 ? (
          <div className="p-10 rounded-2xl bg-slate-900/50 border-2 border-dashed border-slate-800 text-center space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-800 text-[#00E5FF] flex items-center justify-center mx-auto">
              <Sparkles className="w-6 h-6" />
            </div>
            <h4 className="font-bold text-white text-sm">Belum Ada Item Spesial Untukmu</h4>
            <p className="text-xs text-slate-400 max-w-md mx-auto">
              Tambahkan item kurasi khusus untuk ditampilkan pada section &ldquo;Spesial Untukmu&rdquo; di homepage customer.
            </p>
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="px-5 py-2.5 rounded-xl bg-[#00E5FF] text-slate-950 font-bold text-xs uppercase cursor-pointer transition-all hover:bg-[#3cf0ff]"
            >
              + Tambah Item Pertama
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3.5">
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === items.length - 1;

              return (
                <div
                  key={item.id || `special-row-${index}`}
                  className={`p-3.5 sm:p-4 rounded-2xl bg-slate-900/90 border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    item.is_active ? 'border-slate-800' : 'border-slate-800/40 opacity-60'
                  }`}
                >
                  {/* Left: Thumbnail & Info */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-24 sm:w-28 aspect-[4/3] rounded-xl bg-slate-950 border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center relative">
                      {item.image_url ? (
                        <img
                          src={resolveMediaUrl(item.image_url)}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <Images className="w-5 h-5 text-slate-600" />
                      )}

                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-slate-950/80 text-[9px] font-mono font-bold text-white border border-slate-700">
                        #{item.display_order || index + 1}
                      </span>
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-sm text-white truncate">
                          {item.title}
                        </h4>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase ${
                            item.is_active
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                              : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {item.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </div>
                      <p className="text-xs font-mono font-bold text-[#00E5FF] mt-0.5">
                        {formatRupiah(item.price)}
                      </p>
                      {item.description && (
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {item.description}
                        </p>
                      )}
                      <p className="text-[10px] font-mono text-slate-500 mt-1">
                        Urutan Tampil: {item.display_order || index + 1}
                      </p>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    {/* Reorder Arrows */}
                    <div className="flex items-center bg-slate-950 rounded-lg p-0.5 border border-slate-800">
                      <button
                        type="button"
                        disabled={isFirst}
                        onClick={() => handleMoveUp(index)}
                        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        title="Geser ke Atas"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={isLast}
                        onClick={() => handleMoveDown(index)}
                        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        title="Geser ke Bawah"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Toggle Active */}
                    <button
                      type="button"
                      onClick={() => handleToggleActive(item.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-colors cursor-pointer border ${
                        item.is_active
                          ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                          : 'bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-400 border-emerald-800/50'
                      }`}
                    >
                      {item.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                    </button>

                    {/* Edit Button */}
                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(item)}
                      className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer border border-slate-700"
                      title="Edit Item"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>

                    {/* Delete Confirmation / Button */}
                    {pendingDeleteId === item.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleConfirmDelete(item.id)}
                          className="px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold cursor-pointer"
                        >
                          Ya, Hapus
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeleteId(null)}
                          className="px-2 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-[11px] cursor-pointer"
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(item.id)}
                        className="p-2 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 transition-colors cursor-pointer border border-rose-800/40"
                        title="Hapus Item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Form for Add / Edit Special For You Item */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-5 text-white my-8">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#00E5FF]" />
                <h3 className="font-display font-bold text-base uppercase">
                  {editingItem ? 'Edit Item Spesial Untukmu' : 'Tambah Item Spesial Untukmu'}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1 rounded-lg text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModalForm} className="space-y-4">
              {uploadError && (
                <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-800 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Image Upload Area */}
              <div className="space-y-2">
                <label className="block text-xs font-mono uppercase text-slate-300 font-semibold">
                  Foto Item <span className="text-rose-400">*</span>
                </label>

                <div className="aspect-[16/10] rounded-2xl bg-slate-950 border border-slate-800 overflow-hidden relative flex flex-col items-center justify-center p-4">
                  {formImageUrl ? (
                    <>
                      <img
                        src={resolveMediaUrl(formImageUrl)}
                        alt="Preview Item"
                        className="w-full h-full object-cover rounded-xl"
                        referrerPolicy="no-referrer"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-slate-900/90 hover:bg-slate-900 text-white text-xs font-mono border border-slate-700 shadow-md flex items-center gap-1.5 cursor-pointer"
                      >
                        <Upload className="w-3.5 h-3.5 text-[#00E5FF]" />
                        <span>{isUploading ? 'Mengunggah...' : 'Ganti Foto'}</span>
                      </button>
                    </>
                  ) : (
                    <div className="text-center space-y-2">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 text-[#00E5FF] flex items-center justify-center mx-auto">
                        <Upload className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">Upload Foto Spesial Untukmu</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Format JPG, PNG, atau WEBP (Maks. 10MB)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="px-4 py-2 rounded-xl bg-[#00E5FF] text-slate-950 font-bold text-xs uppercase cursor-pointer"
                      >
                        {isUploading ? 'Mengunggah...' : 'Pilih Gambar'}
                      </button>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Title, Price & Description */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Nama / Judul <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Contoh: Es Kopi Susu Leton Signature"
                    required
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Deskripsi Singkat
                  </label>
                  <textarea
                    rows={2}
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Contoh: Perpaduan espresso house blend dan gula aren asli..."
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Harga (Rp) <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="500"
                    value={formPrice}
                    onChange={(e) => setFormPrice(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    required
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>
              </div>

              {/* Display Order & Active Toggle */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Urutan Tampil
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formDisplayOrder}
                    onChange={(e) => setFormDisplayOrder(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Status Tayang
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormIsActive(!formIsActive)}
                    className={`w-full py-2 px-3 rounded-xl text-xs font-bold font-mono transition-colors flex items-center justify-center gap-2 cursor-pointer border ${
                      formIsActive
                        ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-400'
                        : 'bg-slate-950 border-slate-800 text-slate-400'
                    }`}
                  >
                    {formIsActive ? <Check className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <span>{formIsActive ? 'Aktif di Homepage' : 'Nonaktif'}</span>
                  </button>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isUploading || !formImageUrl.trim() || !formTitle.trim()}
                  className="px-6 py-2 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 font-bold text-xs uppercase cursor-pointer disabled:opacity-50"
                >
                  {editingItem ? 'Simpan Perubahan' : 'Tambahkan Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
